<template>
  <div v-if="isValidObject(data)" :style="`height: ${contentHeight};`" class="pa-4 overflow--y">
    <div class="subheader">
      <div v-if="data.employee" class="subheader__title__container">
        <h2 v-if="data.employee.name" class="subheader__title">
          {{ data.employee.name }}
        </h2>
        <div v-if="data.employee.phone && data.employee.phone.number">
          {{ data.employee.phone.number | phoneWithDDD }}
        </div>
      </div>

      <v-layout align-center>
        <v-flex xs9>
          <div class="subheader__status status">
            <span
              class="status__bullet"
              :class="data && data.voucher && data.voucher.status && `status__bullet--${data.voucher.status}`"
            />
            <span class="status__text">
              <span v-if="employeeDisabled">
                {{ $t(`Views.SearchPhonePage.employeeDisabled`) }}
              </span>
              <span v-else>
                {{ data && data.voucher && data.voucher.status
                  ? $t(`Views.SearchPhonePage.voucherStatus.${data.voucher.status}`)
                  : $t(`Views.SearchPhonePage.voucherStatus.unmappedStatus`)
                }}
              </span>
            </span>
          </div>
        </v-flex>

        <v-flex v-if="isValidObject(data.voucher.voucherAnalyze)" xs3>
          <span
            v-if="data.voucher.voucherAnalyze.corporateVoucher"
            class="subheader__label"
            :class="{ 'subheader__label--semibold error--text': !data.voucher.voucherAnalyze.corporateVoucher }"
          >
            Corp: {{ data.voucher.voucherAnalyze.corporateVoucher }}
          </span>

          <span
            v-if="data.voucher.voucherAnalyze.voucherUS"
            class="subheader__label"
            :class="{ 'subheader__label--semibold error--text': !data.voucher.voucherAnalyze.voucherUS }"
          >
            Cache: {{ data.voucher.voucherAnalyze.voucherUS }}
          </span>
        </v-flex>
      </v-layout>
    </div>

    <div>
      <ul class="status__list">
        <li
          v-if="data.company && data.company.status && data.company.status.text"
          class="status__list__item status"
        >
          <span
            class="status__bullet"
            :class="[
              { 'status__bullet--success': data.company.status.boolean },
              { 'status__bullet--error': !data.company.status.boolean },
            ]"
          />
          <span class="status__text">
            {{ $t(`${namespace}.company`) }}: {{ data.company.status.text }}
          </span>
        </li>

        <li
          v-if="data.costCenter && data.costCenter.status"
          class="status__list__item status"
        >
          <span
            class="status__bullet"
            :class="[
              { 'status__bullet--success': data.costCenter.status === 'enabled' },
              { 'status__bullet--error': data.costCenter.status === 'disabled' },
            ]"
          />
          <span class="status__text">
            {{ $t(`${namespace}.costCenter`) }}: {{ $t(`${namespace}.${data.costCenter.status}`) }}
            <v-tooltip
              v-if="data.costCenter.status === 'disabled'"
              slot="append"
              min-width="200"
              max-width="200"
              class="ml-1"
              top
            >
              <v-icon color="accent" slot="activator" class="ma-0 pa-0" small>info</v-icon>
              <span>
                <ul v-if="isValidArray(data.costCenter.list)" class="tooltip__list">
                  <li v-for="(costCenter, index) in data.costCenter.list" :key="`costCenter-${index}`">
                    {{ costCenter.name }} {{ !costCenter.blocked ? 'ativo' : 'inativo' }}<span v-if="index < data.costCenter.list.length -1">,</span>
                  </li>
                </ul>

                <span v-else>{{ $t(`${namespace}.noLinkedCostCenter`) }}</span>
              </span>
            </v-tooltip>
          </span>
        </li>

        <li
          v-if="data.creditPolicy && data.creditPolicy.status && data.creditPolicy.status === 'disabled'"
          class="status__list__item status"
        >
          <span
            class="status__bullet"
            :class="[
              { 'status__bullet--success': data.creditPolicy.status === 'enabled' },
              { 'status__bullet--error': data.creditPolicy.status === 'disabled' },
            ]"
          />
          <span class="status__text">
            {{ $t(`${namespace}.creditPolicy`) }}: {{
              data.creditPolicy.status === 'disabled'
                ? $t(`${namespace}.budgetDisabled`)
                : $t(`${namespace}.budgetEnabled`)
              }}
          </span>
        </li>

        <li
          v-if="data.timePolicy && data.timePolicy.status && data.timePolicy.status === 'disabled'"
          class="status__list__item status"
        >
          <span
            class="status__bullet"
            :class="[
              { 'status__bullet--success': data.timePolicy.status === 'enabled' },
              { 'status__bullet--error': data.timePolicy.status === 'disabled' },
            ]"
          />
          <span class="status__text">
            {{ $t(`${namespace}.usagePolicy`) }}: {{
              data.timePolicy.status === 'disabled'
                ? $t(`${namespace}.timePolicyDisabled`)
                : $t(`${namespace}.timePolicyEnabled`)
            }}
          </span>
        </li>
      </ul>
    </div>

    <div class="mb-2">
      <div class="item__label text-capitalize">{{ $t(`Common.company`) }}</div>
      <div v-if="data.company && data.company.name">{{ data.company.name }}</div>
      <div v-else>N/A</div>
    </div>

    <div class="mb-2">
      <div class="item__label text-capitalize">CNPJ</div>
      <div v-if="data.company && data.company.cnpj">{{ data.company.cnpj | cnpj }}</div>
      <div v-else>N/A</div>
    </div>

    <div v-if="!employeeDisabled" class="mb-2">
      <div class="item__label">{{ $t(`Common.costCenter`) }}</div>
      <div v-if="isValidArray(data.costCenter.list)">
        <div v-if="data.costCenter.list.length > 4 && costCentersLimit">
          <span v-for="(item, index) in data.costCenter.list.slice(0, 4)" :key="index">
            <a
              v-if="claims.costCenter['ROLE_GET_COSTCENTER_ID']"
              class="accent--text"
              @click.prevent="change({
                type: 'costCenter',
                action: null
              }, {
                id: item.id,
                name: item.name.trim()
              })"
            >
              {{ item.name.trim() }}<span v-if="index !== (data.costCenter.list.slice(0, 4).length - 1)">, </span>
            </a>

            <span v-else>
              {{ item.name.trim() }}<span v-if="index !== (data.costCenter.list.slice(0, 4).length - 1)">, </span>
            </span>
          </span>

          <div>
            <a class="gray-8--text caption" @click="handleToggleCostCentersLimit">
              {{ $t(`${namespace}.viewMore`) }}
            </a>
          </div>
        </div>

        <div v-if="data.costCenter.list.length <= 4 || !costCentersLimit">
          <span v-for="(item, index) in data.costCenter.list" :key="index">
            <a
              v-if="claims.costCenter['ROLE_GET_COSTCENTER_ID']"
              class="accent--text"
              @click.prevent="change({
                type: 'costCenter',
                action: null
              }, {
                id: item.id,
                name: item.name.trim()
              })"
            >
              {{ item.name.trim() }}<span v-if="index !== (data.costCenter.list.length - 1)">, </span>
            </a>

            <span v-else>
              {{ item.name.trim() }}<span v-if="index !== (data.costCenter.list.length - 1)">, </span>
            </span>
          </span>

          <div v-if="data.costCenter.list.length > 4">
            <a class="gray-8--text caption" @click="handleToggleCostCentersLimit">
              {{ $t(`${namespace}.seeLess`) }}
            </a>
          </div>
        </div>
      </div>

      <router-link
        v-else-if="claims.employee['ROLE_PUT_EMPLOYEE_ID'] && data.employee.status === 'enabled'"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addCostCenter`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>

    <div v-if="!employeeDisabled" class="mb-3">
      <div class="item__label text-capitalize">{{ $t(`Common.categories`) }}</div>
      <div v-if="isValidArray(data.employee.categories)">
        <span
          v-for="category in data.employee.categories "
          :key="category"
        >
          <v-tooltip v-if="getCategoryData(category)" slot="append" top min-width="177" max-width="177">
            <div slot="activator" class="label-category">
              <img :src="getCategoryData(category).img" alt="">
              <span>{{ getCategoryData(category).text }}</span>
            </div>

            <span>{{ getCategoryData(category).tooltip }}</span>
          </v-tooltip>
        </span>
      </div>

      <router-link
        v-else-if="claims.employee['ROLE_PUT_EMPLOYEE_ID']"
        :to="{
          name: 'Employee',
          params: {
            type: 'editar',
            id: data.employee.id
          }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.addCategories`) }}
      </router-link>

      <div v-else>N/A</div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { isObject, isArray, calcContentHeight, objectToArray } from 'utils/functions'

export default {
  name: 'Preview',
  props: {
    data: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    ...mapState({
      claims: ({ userClaims }) => userClaims.claims
    }),

    employeeDisabled () {
      return this.data && this.data.employee && this.data.employee.status === 'disabled'
    }
  },
  data () {
    return {
      namespace: 'Components.GlobalDrawer.voucher.preview',
      contentHeight: 'none',
      costCentersLimit: true
    }
  },
  methods: {
    isValidObject (val) {
      return isObject(val)
    },

    isValidArray (val) {
      return isArray(val)
    },

    calcContentHeight () {
      this.contentHeight = `${calcContentHeight()}px`
    },

    handleToggleCostCentersLimit () {
      this.costCentersLimit = !this.costCentersLimit
    },

    getCategoryData (category) {
      if (!category) return
      /* todo: ridesCategoriesList */
      return objectToArray(this.$store.state.commonConfig.categories).find(item => item.value === category)
    },

    change (drawer, data) {
      this.$emit('change', {
        drawer: {
          type: drawer.type,
          action: drawer.action
        },
        data: {
          id: data.id,
          name: data.name
        }
      })
    }
  },
  mounted () {
    this.calcContentHeight()
    window.addEventListener('resize', this.calcContentHeight)
  },
  beforeDestroy () {
    window.addEventListener('resize', this.calcContentHeight)
  }
}
</script>

<style lang="scss" scoped>
.subheader__title__container {
  padding: 0 0 24px;
  text-align: center;
}

.subheader__title {
  color: $gray-7;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 800;
}

.subheader {
  border-bottom: 1px solid rgba(0, 0, 0, 0.13);
  margin-bottom: 24px;
  padding-bottom: 24px;
}

.subheader__label {
  display: block;
  font-size: 10px;

  &.subheader__label--semibold  {
    font-weight: 600;
  }
}

.subheader__status {
  border-radius: 16px;
  background-color: #e5e5e5;
  height: 32px;
  padding: 0 12px 0 8px;
}

.status {
  align-items: center;
  display: inline-flex;
}

.status__bullet {
  background-color: #ccc;
  border-radius: 50%;
  height: 16px;
  margin-right: 8px;
  width: 16px;

  &.status__bullet--available,
  &.status__bullet--success {
    background-color: #b4ec51;
    background-image: linear-gradient(135deg, #b4ec51, #2dcc70);
  }

  &.status__bullet--notSynchronized,
  &.status__bullet--unavailable,
  &.status__bullet--error {
    background-color: $error;
    background-image: linear-gradient(135deg, $error, #9f041b);
  }
}

.status__text {
  color: $gray-7;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.status__list {
  list-style-type: none;
  padding: 0 0 8px;
}

.status__list__item {
  align-items: center;
  display: flex;
  margin: 16px 0;

  .status__bullet,
  .status__text {
    align-items: center;
    display: flex;
  }

  .status__text {
    line-height: normal;
  }
}

.tooltip__list {
  list-style-type: none;
  margin: 0;
  padding: 0;
}
</style>
